import { App, Plugin, watch, computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';

import type { Auth } from 'vue-auth3';
import type { Router } from 'vue-router';

import type { AuthUser, Event, Team, ActiveContextItem, ContextEntry, ContextItem, ContextMode } from '@lib/types';
import { useAppOptionsStore } from '@lib/stores/app-options';
import {
	activeEntry,
	activeId,
	activePath,
	contextCan,
	contextEntry,
	contextKeyOf,
	contextMode,
	contextPath,
	isAllActive,
	isLegacyPayload,
	normalizeContextKey,
	recordPath,
	type ContextSource,
} from '@lib/utils/context';

export type ContextChangeOptions = {
	/**
	 * Whether the change may take the user off the route they are on. A switch from the header
	 * may - the route can name a record of the context that was just left. A switch that is
	 * itself part of a navigation (the `?context=` deep link) may not, or the redirect would
	 * cancel the navigation that triggered it.
	 */
	redirect: boolean;
};

/** One switch of a context or a mode: its key or name, and the id or value before and after. */
export type ContextSwitch = {
	/** Context key (`team`) or mode name (`phase`). */
	key: string;
	/** Id, `'all'` or mode value before; null for none. */
	from: string | null;
	/** Id, `'all'` or mode value after; null for none. */
	to: string | null;
};

/** Runs after a switch of the context or mode it was registered for. */
export type ContextSwitchListener = (change: ContextSwitch) => void | Promise<void>;

/** Runs before any switch; returning false cancels it. */
export type BeforeContextSwitchListener = (change: ContextSwitch) => boolean | void | Promise<boolean | void>;

type AppContextState = {
	enabled: boolean;
	activeContext?: ActiveContextItem[];
	currentContextIds?: Array<string | null>;
	onChangeListener?: (options: ContextChangeOptions) => void | Promise<void>;
	contexts?: ContextEntry[];
	/** The modes of the user's contexts, the entries of the auth user so a mode change shows in both. */
	contextModes?: ContextMode[];
	/** The user's global permissions, for can(). */
	permissions?: string[];
};

/**
 * The active contexts of the signed in user - teams, events and whatever an app defines - with
 * their paths, permissions and modes, and the switching between them. Generic by context key;
 * the team and event getters are wrappers kept for the apps.
 */
export const useAppContextStore = defineStore('appContext', () => {
	// Private state
	const state = reactive<AppContextState>({
		enabled: false,
	});

	// Not state: functions, and nothing to show in the devtools
	const switchListeners = new Map<string, Set<ContextSwitchListener>>();
	const beforeListeners = new Set<BeforeContextSwitchListener>();

	const source = computed<ContextSource>(() => ({
		contexts: state.contexts,
		active_context: state.activeContext,
		context_modes: state.contextModes,
		permissions: state.permissions,
	}));

	/*
	 * Generic getters, by context key (`team`; `teams`, `Team` and the class are accepted)
	 */

	/** The key of a kind for any of its names. */
	const normalizeKey = (key: string): string => normalizeContextKey(key, state.contexts);

	/** The active entry of a kind, with its context roles and permissions. */
	const getActive = (key: string): ActiveContextItem | undefined => activeEntry(source.value, key);

	/** Id of the active record of a kind, `'all'`, or null. */
	const getActiveId = (key: string): string | null => activeId(source.value, key);

	/** The active record of a kind as listed in the switcher, the "all" entry included. */
	const getRecord = (key: string): ContextItem | null => {
		const id = getActiveId(key);
		return contextEntry(source.value, key)?.data.find((d) => String(d.id) === id) ?? null;
	};

	/** Whether a kind is set to "all". */
	const isAll = (key: string): boolean => isAllActive(source.value, key);

	/** `<segment>/<id>` of the active record of a kind, '' for "all" or none. */
	const getPath = (key: string): string => activePath(source.value, key);

	/** `<segment>/<id>` of any record of a kind, '' without an id or for "all". */
	const getRecordPath = (key: string, id?: string | number | null): string => recordPath(source.value, key, id);

	/** Context permission on the active record of a kind, else the global one, else `context.bypass`. */
	const can = (key: string, permission: string): boolean => contextCan(source.value, key, permission);

	/** A mode by name. */
	const getMode = (name: string): ContextMode | undefined => contextMode(source.value, name);

	/*
	 * Legacy getters, by position of the payload
	 */
	const getCurrentContext = computed<Array<ContextItem | null>>(() =>
		(state.contexts ?? []).map((entry) => getRecord(contextKeyOf(entry))),
	);

	const getCurrentContextMap = computed<Record<string, ContextItem | null>>(() => {
		const res: Record<string, ContextItem | null> = {};
		for (const entry of state.contexts ?? []) {
			res[entry.key] = getRecord(contextKeyOf(entry));
		}
		return res;
	});

	/*
	 * Specific context data, wrappers of the generic getters
	 */
	const getEvent = computed<Event | null>(() =>
		isAll('event') ? null : ((getActive('event')?.data as Event) ?? null),
	);

	const getTeam = computed<Team | null>(() => (isAll('team') ? null : ((getActive('team')?.data as Team) ?? null)));

	const getEventContextPath = computed<string>(() => getPath('event'));

	const getTeamContextPath = computed<string>(() => getPath('team'));

	/** The API prefix of the whole selection, parents first; "all" leaves its segment out. */
	const getContextPath = computed<string>(() => contextPath(source.value));

	const getContexts = computed<ContextEntry[]>(() => state.contexts || []);

	/** The modes whose context has an active record or "all". */
	const getActiveModes = computed<ContextMode[]>(() =>
		(state.contextModes ?? []).filter((mode) => getActiveId(mode.for) !== null),
	);

	// Actions
	const enable = () => {
		state.enabled = true;
	};

	const setContexts = (contexts: ContextEntry[]) => {
		state.contexts = contexts;
	};

	/** Takes the contexts, the selection, the modes and the permissions of a user payload. */
	const setUser = (user: AuthUser | null) => {
		state.contexts = user?.contexts ?? undefined;
		state.activeContext = user?.active_context;
		state.currentContextIds = user?.active_context?.map((c) => (c.id === null ? null : String(c.id))) ?? [];
		state.contextModes = user?.context_modes ?? [];
		state.permissions = user?.permissions ?? [];
	};

	const setOnChangeListener = (listener: (options: ContextChangeOptions) => void | Promise<void>) => {
		state.onChangeListener = listener;
	};

	/** Registers a listener for the switches of a context key or mode name; returns its removal. */
	const addSwitchListener = (key: string, listener: ContextSwitchListener): (() => void) => {
		// Kept as given and matched when run, the payload may not be there yet
		if (!switchListeners.has(key)) switchListeners.set(key, new Set());
		switchListeners.get(key)!.add(listener);
		return () => switchListeners.get(key)?.delete(listener);
	};

	/** Registers a listener asked before every switch; returns its removal. */
	const addBeforeSwitchListener = (listener: BeforeContextSwitchListener): (() => void) => {
		beforeListeners.add(listener);
		return () => beforeListeners.delete(listener);
	};

	/** The ids of every kind and the values of every mode. */
	const snapshot = (): Record<string, string | null> => {
		const res: Record<string, string | null> = {};
		for (const entry of state.contexts ?? []) {
			res[contextKeyOf(entry)] = getActiveId(contextKeyOf(entry));
		}
		for (const mode of state.contextModes ?? []) {
			res[mode.name] = mode.value;
		}
		return res;
	};

	/** Whether every before listener lets the switch pass, asked one after the other. */
	const passesBefore = async (change: ContextSwitch): Promise<boolean> => {
		for (const listener of [...beforeListeners]) {
			if ((await listener(change)) === false) return false;
		}
		return true;
	};

	/** Runs the listeners of every key whose value differs; one failing does not stop the rest. */
	const runSwitchListeners = async (before: Record<string, string | null>, after: Record<string, string | null>) => {
		for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
			const from = before[key] ?? null;
			const to = after[key] ?? null;
			if (from === to) continue;

			const listeners = [...switchListeners.entries()]
				.filter(([registered]) => registered === key || normalizeKey(registered) === key)
				.flatMap(([, set]) => [...set]);

			for (const listener of listeners) {
				try {
					await listener({ key, from, to });
				} catch (error) {
					console.error(error);
				}
			}
		}
	};

	/**
	 * Switches a context to a record, `'all'` or none, after every before listener passed. The
	 * backend checks the choice; the change listener reloads the user and the view. Resolves to
	 * false when a listener cancelled.
	 *
	 * @param key context key, plural or singular (`team`, `teams`)
	 */
	const changeContext = async (
		key: string,
		id: string | number | null,
		options: Partial<ContextChangeOptions> = {},
	): Promise<boolean> => {
		const k = normalizeKey(key);
		const to = id === null || id === undefined || id === '' ? null : String(id);
		const before = snapshot();

		if (k in before && before[k] === to) return true;
		if (!(await passesBefore({ key: k, from: before[k] ?? null, to }))) return false;

		// Apps on the old user traits take the old fields only
		const data = isLegacyPayload(state.contexts) ? { [`active_${k}_id`]: to } : { active_contexts: { [k]: to } };
		await axios.put('/me', data);

		if (state.onChangeListener) {
			await state.onChangeListener({ redirect: true, ...options });
		}

		// Without a listener nothing reloads the selection, so take the request as done
		const after = snapshot();
		if (!state.onChangeListener) after[k] = to;

		await runSwitchListeners(before, after);
		return true;
	};

	/**
	 * Sets a mode, after every before listener passed. Runs the mode's switch listeners and
	 * neither reloads the user nor the view. Resolves to false when cancelled or unknown.
	 */
	const setMode = async (name: string, value: string | null): Promise<boolean> => {
		const mode = getMode(name);
		if (!mode) return false;

		const from = mode.value;
		if (from === value) return true;
		if (!(await passesBefore({ key: name, from, to: value }))) return false;

		await axios.put('/me', { active_contexts: { [name]: value } });
		mode.value = value;

		await runSwitchListeners({ [name]: from }, { [name]: value });
		return true;
	};

	return {
		state,
		getCurrentContext,
		getCurrentContextMap,
		getContextPath,
		//
		getEvent,
		getTeam,
		getEventContextPath,
		getTeamContextPath,
		//
		getContexts,
		getActiveModes,
		normalizeKey,
		getActive,
		getActiveId,
		getRecord,
		isAll,
		getPath,
		getRecordPath,
		can,
		getMode,
		//
		enable,
		setContexts,
		setUser,
		setOnChangeListener,
		addSwitchListener,
		addBeforeSwitchListener,
		changeContext,
		setMode,
	};
});

/*
 * Create the Vue plugin
 */
export const AppContextPlugin: Plugin = {
	install: (app: App) => {
		// We are not in a vue instance yet, injection based useComposables do not work, pinja stores do
		const auth = app.config.globalProperties.$auth as Auth;
		const router = app.config.globalProperties.$router as Router;
		const store = useAppContextStore();
		const appOptions = useAppOptionsStore();

		// The page a user without any context lands on, unless the app has its own
		if (!router.hasRoute('NoContextAvailable')) {
			// Beside Home, so it renders inside the app's layout
			const parent = router.getRoutes().find((r) => r.name && r.children?.some((c) => c.name === 'Home'));
			const route = {
				path: '/no-context-available',
				name: 'NoContextAvailable',
				component: () => import('@lib/pages/NoEventAvailable.vue'),
			};
			if (parent?.name) router.addRoute(parent.name, route);
			else router.addRoute(route);
		}

		// Register the user update on context change
		store.setOnChangeListener(async ({ redirect }) => {
			// A route that names a record - /realties/:realty_id and the like - carries an id of the
			// context that was just left, so reloading it in place only asks the backend for a row
			// the new context cannot see: a 404, and an error toast on the way out. The dashboard is
			// the one page that holds in every context.
			const leaves = redirect && Object.keys(router.currentRoute.value.params).length > 0;

			// The whole switch happens behind a spinner, and the order is what it is for: the view
			// comes down first, so the page in view is gone before the new context arrives and
			// never loads against either one, and it goes back up once - after the navigation and
			// the new user data - so what the user ends up looking at is loaded a single time.
			appOptions.is_context_switching = true;
			try {
				if (leaves) await router.push({ name: 'Home' });
				await auth.fetch();
				// Now rather than in the watcher, so the switch listeners see the new selection
				store.setUser(auth.user<AuthUser>());
				appOptions.refreshRouterView();
			} finally {
				appOptions.is_context_switching = false;
			}
		});

		// We wait with the setup of this plugin until the user is fully logged in
		watch(
			() => auth.user<AuthUser>(),
			async (user: AuthUser | null) => {
				// Check if user is set and user has context data
				if (user?.contexts) {
					store.setUser(user);

					// "all" is a choice, only a kind without any is not
					const ids = store.state.currentContextIds ?? [];
					const hasValidContext = ids.length > 0 && !ids.includes(null);

					// Fallback if no context is available
					if (!hasValidContext) {
						router.push({ name: 'NoContextAvailable' });
					}

					// Enable the plugin inside the layout
					store.enable();
				}
			},
		);

		// Register the router handler to change context on given query parameter
		// Check for query based context switches, this needs to be here to recognize the move from external routes to internal
		router.beforeEach(async (to) => {
			if (!to.query.context) return true; // skip if context is not in the query parameter, this prevents infinite loops part 1
			if (to.name === 'login') return true; // skip on login page
			if (!auth.check()) return true; // skip if not logged in

			// Get the context from the query and update it
			const { context, ...restQuery } = to.query;
			const { key, id } = JSON.parse(context as string) as { key: string; id: string };

			// Set the new Context and reaload the user data including the context settings. The
			// route we are heading for is the one the link picked for the new context, so the
			// change must not redirect anywhere of its own - that would cancel this navigation.
			store.setUser(auth.user<AuthUser>());
			await store.changeContext(key, id, { redirect: false });
			await auth.fetch();

			// remove the context parameter, but retriggers this call
			return { ...to, query: restQuery };
		});
	},
};
